import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

/**
 * Resolve image URLs: if the URL starts with /static/ it's served by the backend,
 * so prepend the API base. Otherwise return as-is (external URLs, data URIs, etc.).
 */
export function resolveImageUrl(url: string | null | undefined): string {
  if (!url) return "";
  if (url.startsWith("/static/")) return `${API_BASE}${url}`;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // Reject dangerous protocols (javascript:, data:, vbscript:, etc.)
  return "";
}

export interface SiteConfig {
  color_primary: string;
  color_dark: string;
  color_accent: string;
  color_muted: string;
  color_light_bg: string;
  color_body_bg: string;
  color_footer_blog: string | null;
  font_heading: string;
  font_body: string;
  brand_name: string;
  brand_tagline: string | null;
  logo_url: string | null;
  footer_logo_url: string | null;
  blog_image_url: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_address: string | null;
  vat: string | null;
  vat_rate_percent: number;   // aliquota IVA attiva (0 = IVA non applicata)
  page_about_json: string | null;
  page_fiscal_json: string | null;
  page_contact_json: string | null;
}

const defaultConfig: SiteConfig = {
  color_primary: "#12634A",
  color_dark: "#1C4B3B",
  color_accent: "#BF652E",
  color_muted: "#7A9E8E",
  color_light_bg: "#f0ede8",
  color_body_bg: "#ffffff",
  color_footer_blog: null,
  font_heading: "'Playfair Display', serif",
  font_body: "system-ui, -apple-system, sans-serif",
  brand_name: "GRANBORGO",
  brand_tagline: null,
  logo_url: null,
  footer_logo_url: null,
  blog_image_url: null,
  contact_email: "info@granborgo.com",
  contact_phone: "+4967629499360",
  contact_address: "Granborgo GmbH\nAn der Molkerei 8\n56288 Kastellaun, Deutschland",
  vat: "VAT DE462679696",
  vat_rate_percent: 0,
  page_about_json: null,
  page_fiscal_json: null,
  page_contact_json: null,
};

const SiteConfigContext = createContext<SiteConfig>(defaultConfig);

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteConfig>(defaultConfig);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/shop/site-config`)
      .then((r) => r.json())
      .then((data: SiteConfig) => {
        setConfig(data);
        // Apply CSS custom properties to :root
        const root = document.documentElement;
        root.style.setProperty("--color-primary", data.color_primary);
        root.style.setProperty("--color-dark", data.color_dark);
        root.style.setProperty("--color-accent", data.color_accent);
        root.style.setProperty("--color-muted", data.color_muted);
        root.style.setProperty("--color-light-bg", data.color_light_bg);
        root.style.setProperty("--color-body-bg", data.color_body_bg);
        root.style.setProperty("--font-heading", data.font_heading);
        root.style.setProperty("--font-body", data.font_body);
        // Semantic tokens derived from theme
        root.style.setProperty("--color-text", data.color_dark);
        root.style.setProperty("--color-surface", data.color_light_bg);
      })
      .catch(() => {});
  }, []);

  return (
    <SiteConfigContext.Provider value={config}>
      {children}
    </SiteConfigContext.Provider>
  );
}
